import { useModalActions } from '@carboncopyco/components';
import { CheckmarkCircleIcon } from '@carboncopyco/icons';
import type { SpeakerPanelData } from '@carboncopyco/types/desktop';
import { type Colors, TextCaption, notify } from '@carboncopyco/ui';
import { useEffect } from 'react';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import { SpeakerPanelEventsEnum } from '../../shared/enums/SpeakerPanelEventsEnum';
import { insertOnIndex } from '../../util/tab-util';
import GlobalAppModals from '../components/Modals/GlobalAppModals/GlobalAppModals';
import useElectronListener from '../hooks/useElectronListener';
import useInnerViewLoaded from '../hooks/useInnerViewLoaded';
import useSpeakerPanelData from '../hooks/useSpeakerPanelData';
import useSwitchTab from '../hooks/useSwitchTab';
import rendererService from '../services/RendererService';
import { useSpeakerPanelContext, useTabs } from '../store/context';
import { useAnalyticsSession } from '../store/context/AnalyticsSessionContext';

type NotificationData = {
  message: string;
  type: 'success' | 'error' | 'info';
  linkMessage?: {
    link: string;
    linkLabel: string;
  } | null;
};

const textColor = {
  dark: 'text-ds-accent3-100',
  error: 'text-ds-danger-500',
  warning: 'text-ds-secondary-500',
  info: 'text-ds-primary-500',
  success: 'text-ds-accent2-500',
} as Record<string, Colors>;

const GlobalEventListener = ({ children }: { children: JSX.Element }) => {
  const { updateSpeakerPanelDemosInfoData } = useSpeakerPanelData();
  const { setIsSpeakerPanelOpen } = useSpeakerPanelContext();
  const { isSessionIdle } = useAnalyticsSession();
  const { set: modalsSet } = useModalActions();
  const hasAnyGlobalModalOpen = Array.from(modalsSet).length > 0;
  const { globalTabs, globalTabsRef, setGlobalTabs, activeGlobalTabId } =
    useTabs();
  const switchTab = useSwitchTab();

  const handleSendNotification = (...args: any[]) => {
    const { message, type, linkMessage = null } = args[0] as NotificationData;
    if (linkMessage) {
      notify.snackbar(
        {
          type,
          label: message,
          linkLabel: (
            <div
              className="flex items-center ml-2 cursor-pointer pt-2 pb-2"
              onClick={() => {
                window.dsDesktop.shellOpenExternal(linkMessage.link);
              }}
            >
              <TextCaption
                label={linkMessage.linkLabel}
                color={textColor[type]}
                underline
              />
            </div>
          ),
          iconName: type === 'success' ? <CheckmarkCircleIcon /> : undefined,
          hideClose: false,
        },
        {
          position: 'top-right',
        }
      );
      return;
    }
    notify[type](message);
  };

  const handleSpeakerPanelSetActiveItem = async (...args: unknown[]) => {
    if (isSessionIdle || hasAnyGlobalModalOpen) return;

    const speakerPanelId = args[0] as number;
    const speakerPanelData = args[1] as SpeakerPanelData;
    if (!speakerPanelId || !speakerPanelData) return;

    const speakerPanelDataSelectedTab =
      speakerPanelData.demosInfo[speakerPanelData.activePresentationIndex];

    const selectedTab = globalTabs.find(
      (tab) =>
        tab.group &&
        tab.group.id === speakerPanelId &&
        tab.group.mergingOptions?.key === speakerPanelDataSelectedTab.id
    );

    if (!selectedTab) return;

    const selectedTabIsDetached = selectedTab.group?.mergingOptions?.isDetached;

    const selectedTabIsVisibleIfGroupIsInactive =
      !!selectedTab.group?.isVisibleIfGroupIsInactive;

    if (selectedTabIsDetached || selectedTabIsVisibleIfGroupIsInactive) {
      switchTab(selectedTab);
      await updateSpeakerPanelDemosInfoData(speakerPanelId);
      return;
    }

    const activeGlobalTab = globalTabsRef.current.find(
      (tab) => tab.id === activeGlobalTabId
    );

    const activeTabIsAdditionallyAddedBrowserTab =
      !activeGlobalTab?.group?.mergingOptions;
    if (!selectedTabIsDetached && activeTabIsAdditionallyAddedBrowserTab) {
      const tabToClose = activeGlobalTab;

      const updatedTabs = globalTabsRef.current.map((tab) => {
        const isCurrentTab = tab.id === selectedTab.id;
        if (!tab.group?.mergingOptions) return tab;
        if (isCurrentTab) {
          return {
            ...tab,
            hidden: false,
            group: {
              ...tab.group,
              isVisibleIfGroupIsActive: true,
              isVisibleIfGroupIsInactive: false,
              mergingOptions: {
                ...tab.group.mergingOptions,
                isDetached: true,
              },
            },
          };
        }
        return tab;
      });

      const tabToSetActive = updatedTabs.find(
        (tab) => tab.id === selectedTab.id
      );
      if (!tabToSetActive) return;

      const tabsWithoutSelectedTab = updatedTabs.filter(
        (t) => t.id !== selectedTab.id
      );

      const indexOfActiveTab = tabsWithoutSelectedTab.findIndex(
        (t) => t.id === activeGlobalTabId
      );

      const tabsWithoutActiveBrowserTab = tabsWithoutSelectedTab.filter(
        (t) => t.id !== activeGlobalTabId
      );

      const sortedTabs = insertOnIndex(
        indexOfActiveTab,
        tabToSetActive,
        tabsWithoutActiveBrowserTab
      );

      switchTab(tabToSetActive, {
        globalTabs: sortedTabs,
      });

      await updateSpeakerPanelDemosInfoData(speakerPanelId);

      if (!tabToClose?.id) return;
      rendererService.closeGlobalTab(tabToClose);
      return;
    }

    const activeTabIsDetached =
      activeGlobalTab?.group?.mergingOptions?.isDetached;

    if (!selectedTabIsDetached && activeTabIsDetached) {
      const tabToAttach = activeGlobalTab;

      const updatedTabs = globalTabsRef.current.map((tab) => {
        const isSelectedTab = tab.id === selectedTab.id;
        const isTabToAttach = tab.id === tabToAttach.id;

        if (!tab.group?.mergingOptions) return tab;
        if (isTabToAttach) {
          return {
            ...tab,
            hidden: true,
            group: {
              ...tab.group,
              isVisibleIfGroupIsActive: false,
              isVisibleIfGroupIsInactive: false,
              mergingOptions: {
                ...tab.group.mergingOptions,
                isDetached: false,
              },
            },
          };
        }
        if (isSelectedTab) {
          return {
            ...tab,
            hidden: false,
            group: {
              ...tab.group,
              isVisibleIfGroupIsActive: true,
              isVisibleIfGroupIsInactive: false,
              mergingOptions: {
                ...tab.group.mergingOptions,
                isDetached: true,
              },
            },
          };
        }
        return tab;
      });

      const tabsWithoutSelectedTab = updatedTabs.filter(
        (t) => t.id !== selectedTab.id
      );

      const indexOfActiveTab = tabsWithoutSelectedTab.findIndex(
        (t) => t.id === activeGlobalTabId
      );

      const tabToSwitchTo = updatedTabs.find(
        (tab) => tab.id === selectedTab.id
      );

      if (!tabToSwitchTo) return;

      const sortedTabs = insertOnIndex(
        indexOfActiveTab + 1,
        tabToSwitchTo,
        tabsWithoutSelectedTab
      );

      switchTab(tabToSwitchTo, {
        globalTabs: sortedTabs,
      });

      await updateSpeakerPanelDemosInfoData(speakerPanelId);

      return;
    }

    const updatedTabs = globalTabsRef.current.map((tab) => {
      const isPartOfSpeakerPanel = tab.group?.id === speakerPanelId;
      const isSelectedTab = tab.id === selectedTab.id;
      const currentTabIsDetached = tab.group?.mergingOptions?.isDetached;

      if (
        isPartOfSpeakerPanel &&
        tab.group?.mergingOptions?.allowed &&
        !currentTabIsDetached
      ) {
        return {
          ...tab,
          hidden: !isSelectedTab,
          group: {
            ...tab.group,
            isVisibleIfGroupIsActive: isSelectedTab,
            isVisibleIfGroupIsInactive: isSelectedTab,
          },
        };
      }

      return tab;
    });

    const tabsWithoutSelectedTab = updatedTabs.filter(
      (t) => t.id !== selectedTab.id
    );

    const indexOfActiveTab = tabsWithoutSelectedTab.findIndex(
      (t) => t.id === activeGlobalTabId
    );

    const tabToSwitchTo = updatedTabs.find((tab) => tab.id === selectedTab.id);

    if (!tabToSwitchTo) return;

    const sortedTabs = insertOnIndex(
      indexOfActiveTab + 1,
      tabToSwitchTo,
      tabsWithoutSelectedTab
    );

    switchTab(tabToSwitchTo, {
      globalTabs: sortedTabs,
    });

    await updateSpeakerPanelDemosInfoData(speakerPanelId);
  };

  const handleSetToggleButtonState = (...args: unknown[]) => {
    const isOpen = args[0] as boolean;
    setIsSpeakerPanelOpen(isOpen);
  };

  const handleSpeakerPanelDetach = async (...args: unknown[]) => {
    const speakerPanelId = args[0] as number;
    const index = args[1] as number;
    const speakerPanelData =
      await window.dsDesktop.speakerPanel.getData(speakerPanelId);

    const selectedTab = globalTabs.find(
      (tab) =>
        tab.group &&
        tab.group.id === speakerPanelId &&
        tab.group.mergingOptions?.key === speakerPanelData.demosInfo[index].id
    );

    const selectedTabIsVisibleIfGroupIsInactive =
      !!selectedTab?.group?.isVisibleIfGroupIsInactive;

    const isDetached = selectedTab?.group?.mergingOptions?.isDetached;
    if (!selectedTab || isDetached || selectedTabIsVisibleIfGroupIsInactive) {
      return;
    }

    const updatedTabs = globalTabsRef.current.map((tab) => {
      const isCurrentTab = tab.id === selectedTab.id;
      if (!tab.group?.mergingOptions) return tab;
      if (isCurrentTab) {
        return {
          ...tab,
          hidden: false,
          group: {
            ...tab.group,
            isVisibleIfGroupIsActive: true,
            isVisibleIfGroupIsInactive: false,
            mergingOptions: {
              ...tab.group.mergingOptions,
              isDetached: true,
            },
          },
        };
      }
      return tab;
    });

    const groupTabs = updatedTabs.filter(
      (t) => t.group && t.group?.id === selectedTab.group?.id
    );

    const lastGroupTab = groupTabs[groupTabs.length - 1];
    const indexOfLastGroupTab = updatedTabs.findIndex(
      (t) => t.id === lastGroupTab.id
    );
    const tabsWithoutSelected = updatedTabs.filter(
      (t) => t.id !== selectedTab.id
    );

    const tabToSwitchTo = updatedTabs.find((tab) => tab.id === selectedTab.id);
    if (!tabToSwitchTo) return;

    const sortedTabs = insertOnIndex(
      indexOfLastGroupTab,
      tabToSwitchTo,
      tabsWithoutSelected
    );

    switchTab(tabToSwitchTo, {
      globalTabs: sortedTabs,
    });

    if (!tabToSwitchTo.group?.mergingOptions) return;
    await window.dsDesktop.speakerPanel.updateData(speakerPanelId, {
      activePresentationIndex: tabToSwitchTo.group.mergingOptions.index,
    });
    await updateSpeakerPanelDemosInfoData(speakerPanelId);
  };

  const initEvents = () => {
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.ON_NOTIFICATION_MESSAGE,
      handleSendNotification
    );
    window.dsDesktop?.ipcRenderer.on(
      SpeakerPanelEventsEnum.MAIN_TO_RENDERER_SET_TOGGLE_BUTTON_STATE,
      handleSetToggleButtonState
    );
  };

  const destroyEvents = () => {
    window.dsDesktop?.ipcRenderer.removeListener(
      AppEventsEnum.ON_NOTIFICATION_MESSAGE,
      handleSendNotification
    );
    window.dsDesktop?.ipcRenderer.removeListener(
      SpeakerPanelEventsEnum.RENDERER_TO_MAIN_SET_TOGGLE_BUTTON_STATE,
      handleSetToggleButtonState
    );
  };

  useInnerViewLoaded(setGlobalTabs);
  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_RENDERER_SPEAKER_PANEL_SET_ACTIVE_ITEM,
    handleSpeakerPanelSetActiveItem
  );
  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_RENDERER_SPEAKER_PANEL_DETACH,
    handleSpeakerPanelDetach
  );
  useEffect(() => {
    initEvents();
    return () => {
      destroyEvents();
    };
  }, [globalTabs]);

  return (
    <>
      <GlobalAppModals />
      {children}
    </>
  );
};

export default GlobalEventListener;
